import { Trans } from "@lingui/react/macro";
import {
  informationFieldConfigValueSchema,
  patientKeys,
  updateSafetyAlarmText,
} from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useLingui } from "@lingui/react/macro";
import TextArea from "@/components/TextArea/TextArea";
import type z from "zod";

export const EditSafetyAlarmText = ({
  currentSafetyAlarmText,
  onSuccess,
  patientId,
  fieldConfig,
}: {
  currentSafetyAlarmText: string | null | undefined;
  onSuccess: () => void;
  patientId: string;
  fieldConfig: z.infer<typeof informationFieldConfigValueSchema>;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      safetyAlarmText: currentSafetyAlarmText,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (safetyAlarmText: string | null | undefined) =>
      updateSafetyAlarmText(patientId, safetyAlarmText),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const { t } = useLingui();

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.safetyAlarmText);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Uppdaterar information om sociala insatser`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : undefined}
          <Form.Row>
            <TextArea
              label={t`Sociala insatser`}
              showOptionalLabel={
                fieldConfig === informationFieldConfigValueSchema.enum.optional
              }
              errorMessage={errors.safetyAlarmText?.message}
              {...register("safetyAlarmText", {
                required: {
                  value:
                    fieldConfig ===
                    informationFieldConfigValueSchema.enum.required,
                  message: t`Ange information om sociala insatser`,
                },
              })}
            />
          </Form.Row>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
